import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { isLoggedIn, logout } from '../../utils/auth';
import { CiLogin } from "react-icons/ci";
import { HiOutlineUserPlus } from "react-icons/hi2";

function Header() {
  const location = useLocation();
  const isEditInfoPage = location.pathname === '/edit-info';
  const [loginStatus, setLoginStatus] = useState(isLoggedIn());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Update login status when component mounts or location changes
    setLoginStatus(isLoggedIn());

    // Listen for storage changes (when login state changes in other tabs/windows)
    const handleStorageChange = () => {
      setLoginStatus(isLoggedIn());
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [location.pathname]);

  // Close mobile menu when location changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuOpen && !event.target.closest('.mobile-nav-content') && !event.target.closest('.mobile-menu-button')) {
        setMobileMenuOpen(false);
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    logout();
    setLoginStatus(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const isActiveLink = (path) => {
    if (path.some(p => location.pathname.includes(p))) {
      return true;
    }
    return false;
  };

  return (
    <header className="main-header-menu">
      <div className="header-container">
        <div className="header-content">
          {/* Top section with auth buttons */}
          <div className="header-top">
            <div className="auth-buttons">
              {loginStatus ? (
                // Logged in user options
                <>
                  <button
                    onClick={handleLogout}
                    className="auth-button"
                  >
                    <span>로그아웃</span>
                  </button>
                  {!isEditInfoPage && (
                    <Link to="/edit-info" className="auth-link">
                      <span>내정보 수정하기</span>
                    </Link>
                  )}
                </>
              ) : (
                // Not logged in user options
                <>
                  <Link to="/signup" className="auth-link">
                    <HiOutlineUserPlus size={20} />
                    <span>회원가입</span>
                  </Link>
                  <Link to="/login" className="auth-link">
                    <CiLogin size={20} />
                    <span>로그인</span>
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Main header section */}
          <div className="header-main">
            {/* Logo section */}
            <div className="logo-section">
              <Link to="/" className="logo-link">
                <img
                  src="/images/Logo/header-logo.png"
                  alt="파라다이스 추모원 로고"
                  className="logo-image"
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="nav-section">
              <ul className="nav-list">
                <li className="nav-item">
                  <Link
                    to="/ceo-greeting"
                    className={`nav-link ${isActiveLink(['/ceo-greeting']) ? 'active' : ''}`}
                  >
                    추모원 소개
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    to="/overall-view"
                    className={`nav-link ${isActiveLink(['/overall-view']) ? 'active' : ''}`}
                  >
                    둘러보기
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    to="/sky-special-room"
                    className={`nav-link ${isActiveLink(['/sky-special-room', '/sales-info', '/outdoor-hall-sales',
                      '/sky-hall-vip-sales', '/sky-hall-phoenix-room', '/star-hall-sales', '/sunlight-hall-sales', '/s-hall-sales']) ? 'active' : ''}`}
                  >
                    분양안내
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    to="/mutual-assistance-product-info"
                    className={`nav-link ${isActiveLink(['/mutual-assistance-product-info', '/general-funeral-procedures',
                      '/funeral-information-religious', '/funeral-methods', '/funeral-information-other',
                      '/event-procedure', '/opening-relocation', '/service-advantages']) ? 'active' : ''}`}
                  >
                    상조의전
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    to="/cyber-memorial-search"
                    className={`nav-link ${isActiveLink(['/cyber-memorial-search']) ? 'active' : ''}`}
                  >
                    유족광장
                  </Link>
                </li>
                <li className="nav-item">
                  <Link
                    to="/announcements"
                    className={`nav-link ${isActiveLink(['/announcements', '/memorial-news', '/contact-us', '/faq', '/inquiry-details', '/contact-secret-post']) ? 'active' : ''}`}
                  >
                    고객센터
                  </Link>
                </li>
              </ul>
            </nav>

            {/* Mobile Menu Button */}
            <button
              className={`mobile-menu-button ${mobileMenuOpen ? 'active' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle mobile menu"
            >
              <div className="hamburger-line"></div>
              <div className="hamburger-line"></div>
              <div className="hamburger-line"></div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Overlay */}
      <div className={`mobile-nav-overlay ${mobileMenuOpen ? 'active' : ''}`}>
        <div className={`mobile-nav-content ${mobileMenuOpen ? 'active' : ''}`}>
          {/* Mobile Nav Header */}
          <div className="mobile-nav-header">
            <img
              src="/images/Logo/header-logo.png"
              alt="파라다이스 추모원 로고"
              className="mobile-logo"
              onClick={() => {
                navigate('/');
              }}
            />
            <button
              className="close-button"
              onClick={closeMobileMenu}
              aria-label="Close mobile menu"
            >
              ×
            </button>
          </div>

          {/* Mobile Navigation List */}
          <ul className="mobile-nav-list">
            <li className="mobile-nav-item">
              <Link
                to="/ceo-greeting"
                className={`mobile-nav-link ${isActiveLink(['/ceo-greeting']) ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                추모원 소개
              </Link>
            </li>
            <li className="mobile-nav-item">
              <Link
                to="/overall-view"
                className={`mobile-nav-link ${isActiveLink(['/overall-view']) ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                둘러보기
              </Link>
            </li>
            <li className="mobile-nav-item">
              <Link
                to="/sky-special-room"
                className={`mobile-nav-link ${isActiveLink(['/sky-special-room', '/sales-info', '/outdoor-hall-sales',
                  '/sky-hall-vip-sales', '/sky-hall-phoenix-room', '/star-hall-sales', '/sunlight-hall-sales', '/s-hall-sales']) ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                분양안내
              </Link>
            </li>
            <li className="mobile-nav-item">
              <Link
                to="/mutual-assistance-product-info"
                className={`mobile-nav-link ${isActiveLink(['/mutual-assistance-product-info', '/general-funeral-procedures',
                  '/funeral-information-religious', '/funeral-methods', '/funeral-information-other',
                  '/event-procedure', '/opening-relocation', '/service-advantages']) ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                상조의전
              </Link>
            </li>
            <li className="mobile-nav-item">
              <Link
                to="/cyber-memorial-search"
                className={`mobile-nav-link ${isActiveLink(['/cyber-memorial-search']) ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                유족광장
              </Link>
            </li>
            <li className="mobile-nav-item">
              <Link
                to="/announcements"
                className={`mobile-nav-link ${isActiveLink(['/announcements', '/memorial-news', '/contact-us', '/faq']) ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                고객센터
              </Link>
            </li>
          </ul>

          {/* Mobile Auth Section */}
          <div className="mobile-auth-section">
            <div className="mobile-auth-buttons">
              {loginStatus ? (
                // Logged in user options
                <>
                  <button
                    onClick={() => {
                      handleLogout();
                      closeMobileMenu();
                    }}
                    className="mobile-auth-button"
                  >
                    <span>로그아웃</span>
                  </button>
                  {!isEditInfoPage && (
                    <Link
                      to="/edit-info"
                      className="mobile-auth-link"
                      onClick={closeMobileMenu}
                    >
                      <span>내정보 수정하기</span>
                    </Link>
                  )}
                </>
              ) : (
                // Not logged in user options
                <>
                  <Link
                    to="/signup"
                    className="mobile-auth-link"
                    onClick={closeMobileMenu}
                  >
                    <HiOutlineUserPlus size={20} />
                    <span>회원가입</span>
                  </Link>
                  <Link
                    to="/login"
                    className="mobile-auth-link"
                    onClick={closeMobileMenu}
                  >
                    <CiLogin size={20} />
                    <span>로그인</span>
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Consultation Section */}
          <div className="consultation-section">
            <div className="consultation-content">
              <h3 className="consultation-title">분양상담</h3>
              <p className="consultation-description">
                가족분들의 마음의 안식처가 되는 추모의 공간입니다.
              </p>
              <div className="consultation-phone">
                <a href="tel:1599-9442" className="phone-link">1599-9442</a>
              </div>
            </div>
          </div>




        </div>
      </div>
    </header>
  );
}

export default Header; 